import { DataCount, GroupDataResponse, GroupsDataResponse } from '@front-app/services/GroupsApi/GroupDataModel';
import { slidingWindow } from '@front-app/util/array';
import { sum } from '@front-app/util/math';
import { add, differenceInDays } from 'date-fns';
import {
	DataPointName,
	TimeseriesPoint,
	MappedDataResponse,
	DateData,
	EchartsHookState,
} from './model';

export function fillMinMax(
	vals: TimeseriesPoint[],
	minDate: Date,
	fillValMin: number,
	maxDate: Date,
	fillValMax: number,
): TimeseriesPoint[] {
	const output: TimeseriesPoint[] = vals.sort((a, b) => a[0] - b[0]);
	if (vals.length === 0 || vals[0][0] !== minDate.getTime()) {
		output.unshift([minDate.getTime(), fillValMin]);
	}
	if (vals[vals.length - 1][0] !== maxDate.getTime()) {
		output.push([maxDate.getTime(), fillValMax]);
	}
	return output;
}

export const DEFAULT_AVERAGING_WINDOW_DAYS = 7;

export function calculateAllData(
	names: DataPointName[],
	parsedData: MappedDataResponse,
	initialTotal: number,
	minX: Date,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	maxX: Date,
	averagingWindowDays: number,
): [DateData[], DateData[], number] {
	let runningTotal = initialTotal;
	const allDataMap: Map<number, DateData> = new Map([
		[minX.getTime(), {
			date: minX,
			data: getEmptyDataMap(),
			totalAtDate: runningTotal,
			windowedAverageAtDate: 0,
		}],
	]);

	names.forEach((name) => mergeDataPoint(allDataMap, parsedData, name));
	const filledDataMap = new Map([...allDataMap.entries()]);
	fillEmptyDataRows(filledDataMap, minX, maxX);

	const sortedData = Array.from(filledDataMap.entries())
		.sort((a, b) => a[1].date.getTime() - b[1].date.getTime());

	// eslint-disable-next-line no-restricted-syntax
	for (const result of slidingWindow(averagingWindowDays, sortedData)) {
		const windowSum = result
			.flatMap((item) => names.map((n) => item[1].data[n]))
			.reduce(sum, 0);
		const lastKey = result[result.length - 1][0];
		const targetEntry = filledDataMap.get(lastKey);
		if (targetEntry) {
			targetEntry.windowedAverageAtDate = windowSum / averagingWindowDays;
			filledDataMap.set(lastKey, targetEntry);
		}
	}

	const allData = sortedData
		.map(([, v]) => v)
		.map(({ date, data, windowedAverageAtDate }) => {
			runningTotal = Object.values(data)
				.reduce(sum, runningTotal);
			return {
				date,
				data,
				windowedAverageAtDate,
				totalAtDate: runningTotal,
			};
		});

	runningTotal = initialTotal;
	const filledAllData = Array.from(filledDataMap.values())
		.sort((a, b) => a.date.getTime() - b.date.getTime())
		.map(({ date, data, windowedAverageAtDate }) => {
			runningTotal = Object.values(data)
				.reduce(sum, runningTotal);
			return {
				date,
				data,
				windowedAverageAtDate,
				totalAtDate: runningTotal,
			};
		});

	return [allData, filledAllData, runningTotal];
}

function getEmptyDataMap(): Record<DataPointName, number> {
	return {
		[DataPointName.MEMBER_JOINS]: 0,
		[DataPointName.MEMBER_LEAVES]: 0,
		[DataPointName.SHARE_UPLOADS]: 0,
		[DataPointName.SHARE_REMOVES]: 0,
		[DataPointName.CREATE_DISCUSSION]: 0,
		[DataPointName.DISCUSSION_POSTS]: 0,
		[DataPointName.DATE]: 0,
		[DataPointName.CUMULATIVE_TOTAL]: 0,
		[DataPointName.READ_DISCUSSION]: 0,
		[DataPointName.VIEW]: 0,
		[DataPointName.WINDOWED_AVERAGE]: 0,
		[DataPointName.CREATE_EVENT]: 0,
		[DataPointName.FILE_DOWNLOADS]: 0,
	};
}
function mergeDataPoint(
	allDataMap: Map<number, DateData>,
	data: MappedDataResponse,
	name: DataPointName,
): void {
	const dataPoints = data[name]?.dataPoints || [];
	dataPoints
		.forEach(({ date, count }) => {
			const dataPoint: DateData = allDataMap.get(date.getTime()) || {
				date,
				data: getEmptyDataMap(),
				totalAtDate: 0,
				windowedAverageAtDate: 0,
			};
			dataPoint.data[name] = count;
			allDataMap.set(date.getTime(), dataPoint);
		});
}

function fillEmptyDataRows(allDataMap: Map<number, DateData>, minX: Date, maxX: Date): void {
	getAllDays(minX, maxX)
		.forEach((date) => setIfEmpty(allDataMap, date));
}

function setIfEmpty(allDataMap: Map<number, DateData>, date: Date) {
	if (!allDataMap.has(date.getTime())) {
		allDataMap.set(date.getTime(), {
			date,
			data: getEmptyDataMap(),
			totalAtDate: 0,
			windowedAverageAtDate: 0,
		});
	}
}

export function getAllDays(min: Date, max: Date): Date[] {
	const numDays = Math.abs(differenceInDays(min, max) + 1);
	return [...Array(numDays).keys()]
		.map((val) => add(min, { days: val }));
}

export function mergeGroupDataResponses(keyName: string, a: GroupDataResponse, b: GroupDataResponse): GroupDataResponse {
	return {
		keyName,
		totalBeforeStart: a.totalBeforeStart + b.totalBeforeStart,
		dataPoints: mergeDataCounts(a.dataPoints, b.dataPoints),
	};
}

function mergeDataCounts(a: DataCount[], b: DataCount[]): DataCount[] {
	const timeMap = new Map(a.map((item) => ([item.date.getTime(), item])));
	b.forEach((item) => {
		const timestamp = item.date.getTime();
		const existing = timeMap.get(timestamp);
		if (!existing) {
			timeMap.set(timestamp, item);
		} else {
			timeMap.set(timestamp, {
				date: item.date,
				count: item.count + existing.count,
			});
		}
	});
	return Array.from(timeMap.values());
}

export function localDateToUtc(d: Date): Date {
	return new Date(d.getTime() - (d.getTimezoneOffset() * 60 * 1000));
}

export function utcToLocalDate(d: Date): Date {
	return new Date(d.getTime() + (d.getTimezoneOffset() * 60 * 1000));
}

/**
 * Calculates the spacing of charts from arbitrary values into percentages
 *
 * @param chartHeights
 * @param topBuffer
 * @param bottomBuffer
 * @param chartBuffer
 * @return An array of tuples of the form: [item's top's percent distance from root top, item's bottom's percent distance from root bottom]
 */
export function calculateChartSpacingAsPercentages(
	chartHeights: number[],
	topBuffer: number,
	bottomBuffer: number,
	chartBuffer: number,
): Array<[number, number]> {
	const totalHeight = chartHeights.reduce((acc, curr) => acc + curr, 0)
		+ topBuffer
		+ bottomBuffer
		+ (chartBuffer * (chartHeights.length - 1));

	const output = [];
	let usedHeightSoFar = topBuffer;

	for (let i = 0; i < chartHeights.length; i += 1) {
		const curr = chartHeights[i];
		const top = (usedHeightSoFar / totalHeight) * 100;
		usedHeightSoFar += curr;
		const result: [number, number] = [
			top,
			((totalHeight - usedHeightSoFar) / totalHeight) * 100,
		];
		output.push(result);
		usedHeightSoFar += chartBuffer;
	}

	return output;
}

export function extractWindowedSeries(isEnhancedDashboard: boolean, numExcludedPointsFromStart: number, intialData: DateData[]): TimeseriesPoint[] {
	return intialData
		.filter((_, idx) => (idx >= numExcludedPointsFromStart))
		.map(({ date, windowedAverageAtDate }) => {
			if (isEnhancedDashboard) {
				return [date.getTime(), windowedAverageAtDate];
			}
			return [date.getTime(), 0];
		});
}

export function extractDataFromResponse(stateVal: EchartsHookState, allResponses: GroupsDataResponse): GroupDataResponse[] {
	// For now, handle only 1 group
	const singleGroupId = stateVal.groupIds[0];
	if (!singleGroupId) {
		// eslint-disable-next-line no-console
		console.error('At least one group must be selected');
		return [];
	}
	if (stateVal.aggregateGroups) {
		return allResponses.aggregated;
	}
	return allResponses[singleGroupId];
}
